import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useMakeRequest } from "../../hooks/useMakeRequest";
import {
  APPLICATION_API,
  JOBSEEKER_URL,
  OFFER_API,
} from "../../resources/resource";
import { FaSpinner } from "react-icons/fa"; // For loading spinner
import { useFetchData } from "../../hooks/useFtechData";

function MakeOffer({ companyName, companyAddress, Country, phoneNumber }) {
  const { employer, candidate, application } = useParams();
  const makeRequest = useMakeRequest();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [offerData, setOfferData] = useState({
    employer,
    candidate,
    application,
    resumptionTime: "",
    resumptionDate: "",
    amount: "",
  });

  const {
    data: applicantDetails,
    refetch,
    loading: applicantLoading,
  } = useFetchData(`${JOBSEEKER_URL}/getAjobSeeker/${candidate}`);
  const { data: applicationDetails, loading: applicationLoading } =
    useFetchData(`${APPLICATION_API}/getAnApplication/${application}`);

  // Sync fetched data with state
  useEffect(() => {
    if (applicantDetails) setApplicantInfo(applicantDetails);
    if (applicationDetails) setApplicationInfo(applicationDetails);
  }, [applicantDetails, applicationDetails]);

  const [applicantInfo, setApplicantInfo] = useState({});
  const [applicationInfo, setApplicationInfo] = useState({});

  const handleForm = (e) => {
    const { name, value } = e.target;
    setOfferData((prev) => ({ ...prev, [name]: value }));
  };

  const handleOffer = async (e) => {
    e.preventDefault();
    if (
      !offerData.resumptionDate ||
      !offerData.resumptionTime ||
      !offerData.amount
    ) {
      setError("Please fill out all fields.");
      return;
    }
    setLoading(true);
    setError(null);
    makeRequest(
      OFFER_API + "/createOffer",
      "POST",
      offerData,
      () => {
        console.log();
      },
      (error) => {
        console.log(error);
      },
      () => {
        setLoading(false);
      }
    );
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-100 p-4">
      <div className="bg-white shadow-md rounded-lg p-6 w-full max-w-3xl">
        {/* Header */}
        <h1 className="text-2xl md:text-3xl font-bold text-center text-gray-800 uppercase mb-6">
          Resumption Confirmation
        </h1>

        {/* Applicant Info */}
        <div className="text-right text-sm text-gray-600 mb-6">
          <p>
            {applicantInfo?.firstName} {applicantInfo?.lastName}
          </p>
          <p>
            {applicantInfo?.localGovernment}, {applicantInfo?.state}
          </p>
          <p>{applicantInfo?.country}</p>
          <p>{applicantInfo?.phoneNumber}</p>
        </div>

        {/* Form Inputs */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resumption Time
            </label>
            <input
              type="time"
              name="resumptionTime"
              value={offerData.resumptionTime}
              onChange={handleForm}
              className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none p-1 transition-colors"
              disabled={loading}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Resumption Date
            </label>
            <input
              type="date"
              name="resumptionDate"
              value={offerData.resumptionDate}
              onChange={handleForm}
              className="w-full border-b-2 border-gray-300 focus:border-blue-500 outline-none p-1 transition-colors"
              disabled={loading}
            />
          </div>
        </div>

        {/* Offer Details */}
        <div className="space-y-4 text-gray-700 mb-6">
          <p>
            After your interview with{" "}
            <span className="font-semibold">{companyName}</span>, you have been
            shortlisted for employment due to your qualifications for this
            position.
          </p>
          <p>
            You are expected to resume on{" "}
            <span className="underline">
              {offerData.resumptionDate || "_______"}
            </span>{" "}
            by{" "}
            <span className="underline">
              {offerData.resumptionTime || "_______"}
            </span>
            . Failure to comply will not be tolerated.
          </p>
          <p>
            You are offered a salary of{" "}
            <span className="underline">
              N{" "}
              <input
                type="number"
                name="amount"
                value={offerData.amount}
                onChange={handleForm}
                className="inline-block w-24 border-b-2 border-gray-300 focus:border-blue-500 outline-none p-1"
                placeholder="Amount"
                disabled={loading}
              />
            </span>{" "}
            per month. After a probation period of 3-6 months, your salary will
            be reviewed based on performance.
          </p>
          <p>For more information, please call {phoneNumber}.</p>
        </div>

        {/* Company Signature */}
        <div className="text-sm text-gray-600 mb-6">
          <p className="font-semibold">Yours Faithfully,</p>
          <p>{applicationInfo?.companyId?.companyName}</p>
          <p>
            {applicationInfo?.companyId?.localGovernment},{" "}
            {applicationInfo?.companyId?.state}
          </p>
        </div>

        {/* Actions */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
          <button
            onClick={handleOffer}
            disabled={loading || applicantLoading || applicationLoading}
            className="w-full sm:w-auto flex items-center justify-center px-6 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 transition-colors"
          >
            {loading ? <FaSpinner className="animate-spin mr-2" /> : null}
            {loading ? "Sending..." : "Send Offer"}
          </button>
          <Link
            to={`/employer/${employer}`}
            className="w-full sm:w-auto px-6 py-2 bg-red-500 text-white rounded-md hover:bg-red-600 text-center transition-colors"
          >
            Back to Profile
          </Link>
        </div>

        {/* Error Message */}
        {error && <p className="text-red-500 text-center mt-4">{error}</p>}
      </div>
    </div>
  );
}

export default MakeOffer;
